/* Tradeline product compare: floating bar + side-by-side modal.
 * Items live in localStorage via Tradeline.compare (tl-theme.js); each product's cells come
 * from /products/<handle>?view=compare (templates/product.compare.liquid).
 */
(() => {
  const T = window.Tradeline || (window.Tradeline = {});
  const FIELDS = ['price', 'vendor', 'availability', 'sku'];
  const FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

  const el = (tag, attrs = {}, html) => {
    const node = document.createElement(tag);
    Object.entries(attrs).forEach(([k, v]) => { if (v !== undefined && v !== null) node.setAttribute(k, v); });
    if (html !== undefined) node.innerHTML = html;
    return node;
  };
  const norm = (s) => String(s || '').replace(/\s+/g, ' ').trim().toLowerCase();

  class TlCompare extends HTMLElement {
    connectedCallback() {
      this.modal = document.querySelector('[data-compare-modal]');
      this.body = this.modal && this.modal.querySelector('[data-compare-body]');
      this.cache = new Map();
      this.render = this.render.bind(this);
      this.onKey = this.onKey.bind(this);

      this.addEventListener('click', (e) => {
        if (e.target.closest('[data-compare-open]')) this.open(e.target.closest('[data-compare-open]'));
        else if (e.target.closest('[data-compare-clear]')) T.compare.clear();
      });
      if (this.modal) {
        this.modal.addEventListener('click', (e) => this.onModalClick(e));
        const diff = this.modal.querySelector('[data-compare-diff]');
        if (diff) diff.addEventListener('change', () => this.modal.classList.toggle('is-diff-only', diff.checked));
      }
      document.addEventListener('tl:compare:changed', this.render);
      this.render();
    }

    disconnectedCallback() { document.removeEventListener('tl:compare:changed', this.render); }

    items() { return T.compare ? T.compare.items() : []; }

    /* ---------- Bar ---------- */
    render() {
      const items = this.items();
      this.hidden = items.length === 0;
      const count = this.querySelector('[data-compare-count]');
      if (count) count.textContent = items.length;
      const thumbs = this.querySelector('[data-compare-thumbs]');
      if (thumbs) {
        thumbs.innerHTML = '';
        items.forEach((item) => {
          if (!item.image) return;
          thumbs.appendChild(el('img', { src: item.image, alt: item.title || '', width: 34, height: 34, loading: 'lazy' }));
        });
      }
      const openBtn = this.querySelector('[data-compare-open]');
      if (openBtn) openBtn.disabled = items.length < 1;
      if (this.isOpen()) {
        if (items.length === 0) this.close();
        else this.build();
      }
    }

    /* ---------- Modal ---------- */
    isOpen() { return this.modal && !this.modal.hidden; }

    open(opener) {
      if (!this.modal) return;
      this.opener = opener || document.activeElement;
      this.modal.hidden = false;
      document.documentElement.classList.add('tl-locked');
      document.addEventListener('keydown', this.onKey);
      const panel = this.modal.querySelector('.tl-compare-modal__panel');
      if (panel) panel.focus();
      this.build();
    }

    close() {
      if (!this.modal || this.modal.hidden) return;
      this.modal.hidden = true;
      document.removeEventListener('keydown', this.onKey);
      if (!document.querySelector('.tl-drawer.is-open')) document.documentElement.classList.remove('tl-locked');
      const target = this.opener && document.contains(this.opener) && !this.hidden ? this.opener : this.querySelector('[data-compare-open]');
      if (target && !this.hidden) target.focus();
    }

    onKey(e) {
      if (!this.isOpen()) return;
      if (e.key === 'Escape') { e.preventDefault(); this.close(); return; }
      if (e.key !== 'Tab') return;
      const nodes = Array.from(this.modal.querySelectorAll(FOCUSABLE)).filter((n) => n.offsetParent !== null || n === document.activeElement);
      if (!nodes.length) return;
      const first = nodes[0];
      const last = nodes[nodes.length - 1];
      if (e.shiftKey && (document.activeElement === first || !this.modal.contains(document.activeElement))) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    }

    onModalClick(e) {
      if (e.target === this.modal || e.target.closest('[data-compare-close]')) { this.close(); return; }
      const remove = e.target.closest('[data-compare-remove]');
      if (remove) {
        const handle = remove.dataset.compareRemove;
        const item = this.items().find((i) => i.handle === handle);
        if (item) T.compare.toggle(item);
        return;
      }
      const add = e.target.closest('[data-compare-add]');
      if (add) this.addToCart(add);
    }

    async addToCart(btn) {
      btn.classList.add('is-loading');
      try {
        const data = await T.cart.add([{ id: Number(btn.dataset.compareAdd), quantity: 1 }], 'compare');
        if (T.settings.cartType === 'drawer' || T.settings.cartType === 'notification') this.close();
        T.afterAdd(data, btn, this.modal.dataset.strAdded);
      } catch (err) {
        T.toast(err.message || this.modal.dataset.strError);
      } finally {
        btn.classList.remove('is-loading');
      }
    }

    async fetchItem(handle) {
      if (this.cache.has(handle)) return this.cache.get(handle);
      const root = (T.routes && T.routes.root ? T.routes.root : '/').replace(/\/$/, '');
      const res = await fetch(`${root}/products/${encodeURIComponent(handle)}?view=compare`);
      if (!res.ok) throw new Error('HTTP ' + res.status);
      const doc = new DOMParser().parseFromString(await res.text(), 'text/html');
      const node = doc.querySelector('[data-compare-item]');
      if (!node) throw new Error('No compare data');
      this.cache.set(handle, node);
      return node;
    }

    async build() {
      if (!this.body) return;
      const items = this.items();
      const s = this.modal.dataset;
      this.body.setAttribute('aria-busy', 'true');
      if (!this.body.querySelector('table')) this.body.innerHTML = `<p class="tl-muted">${s.strLoading || '…'}</p>`;
      const results = await Promise.all(items.map((i) => this.fetchItem(i.handle).catch(() => null)));
      const products = results.filter(Boolean);
      this.body.setAttribute('aria-busy', 'false');
      if (!products.length) { this.body.innerHTML = `<p class="tl-muted">${s.strError}</p>`; return; }

      const table = el('table', { class: 'tl-compare-table' });
      const colgroupWidth = Math.max(180, Math.floor(900 / products.length));
      table.style.setProperty('--tl-compare-col', colgroupWidth + 'px');

      /* Header row: image, title, remove */
      const thead = el('thead');
      const headRow = el('tr');
      headRow.appendChild(el('td', { class: 'tl-compare-table__corner' }));
      products.forEach((p) => {
        const td = el('th', { scope: 'col' });
        const image = p.querySelector('[data-field="image"]');
        const title = p.querySelector('[data-field="title"]');
        td.innerHTML = (image ? image.innerHTML : '') + (title ? title.innerHTML : '');
        const btn = el('button', {
          type: 'button',
          class: 'tl-compare-table__remove tl-small',
          'data-compare-remove': p.dataset.handle,
          'aria-label': `${s.strRemove}: ${p.dataset.title}`
        }, s.strRemove);
        td.appendChild(btn);
        headRow.appendChild(td);
      });
      thead.appendChild(headRow);
      table.appendChild(thead);

      const tbody = el('tbody');
      const addRow = (label, cells, isSpec) => {
        const values = cells.map((c) => c.value);
        const present = values.filter((v) => v !== null);
        const differs = products.length > 1 && (present.length !== values.length || new Set(present.map(norm)).size > 1);
        const tr = el('tr', { class: differs ? 'is-diff' : null, 'data-spec': isSpec ? '' : null });
        tr.appendChild(el('th', { scope: 'row' }, ''));
        tr.firstChild.textContent = label;
        cells.forEach((c) => tr.appendChild(el('td', {}, c.html)));
        tbody.appendChild(tr);
      };

      const labels = { price: s.strPrice, vendor: s.strVendor, availability: s.strAvailability, sku: s.strSku };
      FIELDS.forEach((f) => {
        const cells = products.map((p) => {
          const node = p.querySelector(`[data-field="${f}"]`);
          return node ? { html: node.innerHTML, value: node.dataset.value || node.textContent } : { html: '–', value: null };
        });
        if (cells.every((c) => !c.value || !String(c.value).trim())) return;
        addRow(labels[f], cells, f !== 'price');
      });

      /* Union of spec keys, in first-seen order */
      const keys = [];
      const keyLabels = {};
      products.forEach((p) => p.querySelectorAll('[data-spec-key]').forEach((n) => {
        const k = n.dataset.specKey;
        if (!keyLabels[k]) { keys.push(k); keyLabels[k] = n.dataset.specLabel || k; }
      }));
      keys.forEach((k) => {
        const cells = products.map((p) => {
          const node = p.querySelector(`[data-spec-key="${CSS.escape(k)}"]`);
          return node ? { html: node.innerHTML, value: node.dataset.specValue } : { html: '–', value: null };
        });
        addRow(keyLabels[k], cells, true);
      });

      /* Actions row */
      const actions = el('tr', { class: 'tl-compare-table__actions' });
      actions.appendChild(el('th', { scope: 'row' }, ''));
      products.forEach((p) => {
        const cart = p.querySelector('[data-field="cart"]');
        actions.appendChild(el('td', {}, cart ? cart.innerHTML : ''));
      });
      tbody.appendChild(actions);
      table.appendChild(tbody);

      const wrap = el('div', { class: 'tl-compare-table__wrap' });
      wrap.appendChild(table);
      this.body.innerHTML = '';
      if (products.length < 2) this.body.appendChild(el('p', { class: 'tl-small tl-muted tl-compare-modal__hint' }, s.strNeedMore));
      this.body.appendChild(wrap);
    }
  }

  if (!customElements.get('tl-compare')) customElements.define('tl-compare', TlCompare);
})();
